import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { ArrowDown } from "lucide-react"
import { isEmpty } from "radash"

import type {
  Rating,
  RestaurantRatingResponse,
} from "@/lib/api/restaurant-service"
import { getRestaurantRating } from "@/lib/client/api/restaurant"
import { formatRatingCount } from "@/lib/utils"

interface RestaurantRatingSectionProps {
  restaurantId: number
}

function StarRating({ rating }: { rating: number }) {
  return (
    <span className="inline-block text-black">
      {Array.from({ length: 5 }).map((_, i) => (
        <span
          key={i}
          className={i < rating ? "text-be-yellow-500" : "text-gray-300"}
        >
          ★
        </span>
      ))}
    </span>
  )
}

function ReviewCard({ review }: { review: Rating }) {
  return (
    <div className="border border-gray-200 rounded-3xl p-5 flex flex-col">
      <div className="font-bold text-base mb-1">{review.user_name}</div>
      <div className="flex items-center gap-2 text-base text-black/80 mb-1">
        <StarRating rating={review.rating} />
        <span className="mx-1 text-gray-400">•</span>
        <span className="text-gray-500 text-xs">{review.rated_at}</span>
      </div>
      {!isEmpty(review.feedback) && (
        <div className="text-sm text-gray-800 leading-snug">
          {review.feedback}
        </div>
      )}
    </div>
  )
}

export function RestaurantRatingSection({
  restaurantId,
}: RestaurantRatingSectionProps) {
  const { data: ratingsData, isLoading: isLoadingRatings } =
    useQuery<RestaurantRatingResponse>({
      queryKey: ["restaurantRatings", restaurantId],
      queryFn: () => getRestaurantRating(restaurantId),
    })

  const [showAll, setShowAll] = useState(false)

  if (isLoadingRatings) {
    return (
      <div className="mt-20">
        <h2 className="text-2xl font-bold mb-4">Rating and reviews</h2>
        <div className="h-8 w-40 bg-gray-200 rounded mb-6 animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-28 bg-gray-100 rounded-2xl animate-pulse"
            />
          ))}
        </div>
      </div>
    )
  }

  if (
    !ratingsData ||
    !ratingsData.ratings ||
    ratingsData.ratings.length === 0
  ) {
    return (
      <div className="mt-20" id="rating-section">
        <h2 className="text-2xl font-bold mb-4">Đánh giá và nhận xét</h2>
        <p className="text-gray-500">Chưa có đánh giá nào.</p>
      </div>
    )
  }

  const avgRating = ratingsData.restaurant.total_rating?.toFixed(1) ?? "-"
  const totalRatings = ratingsData.restaurant.review_count ?? 0

  return (
    <div className="mt-20" id="rating-section">
      <h2 className="text-2xl font-bold mb-2">Đánh giá và nhận xét</h2>
      <div className="flex items-center gap-2 text-base font-medium mb-8">
        <span className="text-black">★ {avgRating}</span>
        <span className="text-gray-400">·</span>
        <span className="text-gray-600">
          {formatRatingCount(totalRatings)} lượt đánh giá
        </span>
      </div>
      <div className="flex flex-col md:flex-row gap-3">
        {/* Left column */}
        <div className="w-full md:flex-1 flex flex-col gap-3">
          {(showAll ? ratingsData.ratings : ratingsData.ratings.slice(0, 6))
            .filter((_, index) => index % 2 === 0)
            .map((review) => (
              <ReviewCard key={review.rating_id} review={review} />
            ))}
        </div>

        {/* Right column */}
        <div className="w-full md:flex-1 flex flex-col gap-3">
          {(showAll ? ratingsData.ratings : ratingsData.ratings.slice(0, 6))
            .filter((_, index) => index % 2 === 1)
            .map((review) => (
              <ReviewCard key={review.rating_id} review={review} />
            ))}
        </div>
      </div>
      {!showAll && ratingsData.ratings.length > 6 && (
        <div className="flex justify-center mt-6">
          <button
            className="px-6 py-2 rounded-full border border-gray-300 bg-white text-black font-medium hover:bg-gray-50 transition flex items-center gap-2"
            onClick={() => setShowAll(true)}
          >
            <ArrowDown />
            Xem thêm đánh giá
          </button>
        </div>
      )}
    </div>
  )
}
